import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getVesselBoilerEmissionAnalysis } from "../../../../services/vesselMachineryService";
import { generateDateTicks } from "../../../../services/dataTicks";
import { SlidersHorizontal } from "lucide-react";

const EmissionAnalysisChart = () => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter); // Add voyage filter

  const [selectedParams, setSelectedParams] = useState([
    "CO2",
    "N2O",
    "CH4",
    "GHG",
  ]);
  const [emissionData, setEmissionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Define color codes for each emission parameter
  const emissionColors = {
    CO2: "#566573", // Gray for CO₂
    N2O: "#d35400", // Orange for N₂O
    CH4: "#a93226", // Red for CH₄
    GHG: "#7d3c98", // Purple for GHG (total)
  };

  // Emission parameter labels
  const emissionLabels = {
    CO2: "CO₂",
    N2O: "N₂O",
    CH4: "CH₄",
    GHG: "GHG Intensity",
  };

  // Fetch emission data from API
  useEffect(() => {
    const fetchEmissionData = async () => {
      // Skip if no vessel selected
      if (!currentVessel?._id) {
        setEmissionData(null);
        return;
      }

      // Skip if no voyage selected AND no date range
      if (!selectedVoyage && (!startDate || !endDate)) {
        setEmissionData(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getVesselBoilerEmissionAnalysis(
          currentVessel._id,
          selectedVoyage ? null : startDate, // Pass null for dates if voyage selected
          selectedVoyage ? null : endDate, // Pass null for dates if voyage selected
          selectedVoyage?._id || null // Pass voyage ID if selected
        );

        console.log("API Response Emission Analysis Chart:", response);

        if (response?.status === "success") {
          setEmissionData(response.data);
        } else {
          setError(response?.message || "Failed to fetch emission data");
          setEmissionData(null);
        }
      } catch (err) {
        console.error("Error fetching emission data:", err);
        setError(err.message);
        setEmissionData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEmissionData();
  }, [currentVessel, startDate, endDate, selectedVoyage]); // Add selectedVoyage to dependencies

  // Transform API data to Highcharts format
  const transformDataForChart = () => {
    if (!emissionData?.emissions || emissionData.emissions.length === 0) {
      return {
        dates: [], // Add this for tick generation
        series: [],
      };
    }

    const dates = emissionData.emissions.map((item) => item.date); // Store dates for tick generation

    const seriesData = {
      CO2: [],
      N2O: [],
      CH4: [],
      GHG: [],
    };

    emissionData.emissions.forEach((item) => {
      const timestamp = new Date(item.date).getTime(); // Convert to timestamp

      // Use original kg values from API
      const co2Value = item.emissions.co2.value;
      const n2oValue = item.emissions.n2o.value;
      const ch4Value = item.emissions.ch4.value;
      const ghgValue = item.emissions.ghg.value;

      seriesData.CO2.push({
        x: timestamp, // Use timestamp instead of index
        y: co2Value,
        fullDate: new Date(item.date).toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        color: emissionColors.CO2,
      });

      seriesData.N2O.push({
        x: timestamp,
        y: n2oValue,
        fullDate: new Date(item.date).toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        color: emissionColors.N2O,
      });

      seriesData.CH4.push({
        x: timestamp,
        y: ch4Value,
        fullDate: new Date(item.date).toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        color: emissionColors.CH4,
      });

      seriesData.GHG.push({
        x: timestamp,
        y: ghgValue,
        fullDate: new Date(item.date).toLocaleDateString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        color: emissionColors.GHG,
      });
    });

    // return {
    //   dates, // Return dates for tick generation
    //   series: selectedParams.map((param) => ({
    //     name: emissionLabels[param],
    //     data: seriesData[param],
    //     color: emissionColors[param],
    //     marker: {
    //       enabled: true,
    //       radius: 4,
    //       fillColor: emissionColors[param],
    //     },
    //   })),
    // };
    return {
      dates,
      series: selectedParams.map(param => ({
        name: emissionLabels[param],
        data: seriesData[param],
        color: emissionColors[param],
        yAxis: (param === "GHG" ? 1 : 0), // <-- THIS IS THE FIX
        marker: {
          enabled: true,
          radius: 4,
          fillColor: emissionColors[param],
          lineColor: "#fff",
          lineWidth: 1,
        },
      }))
    };
  };

  const chartData = transformDataForChart();

  // Debug: Check what data we're sending to Highcharts
  console.log("Chart Data:", chartData);
  console.log("Emission Data:", emissionData);

  const tickPositions =
    chartData.dates && chartData.dates.length > 0
      ? generateDateTicks(chartData.dates)
      : [];

  const options = {
    chart: {
      type: "line",
      height: "500",
      backgroundColor: "transparent",
      spacing: [20, 15, 15, 15],
    },
    title: null,
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: {
        text: null,
      },
      labels: {
        format: "{value:%b %e}",
        style: {
          color: "#666666",
          fontSize: "11px",
        },
        rotation: -45,
      },
      gridLineWidth: 1,
      gridLineColor: "#f0f0f0",
    },
    // yAxis: {
    //   title: {
    //     text: "Emissions (MT/day and g CO₂e/MJ)",
    //     style: {
    //       color: "#718096",
    //       fontSize: "12px",
    //       fontWeight: "500",
    //     },
    //   },
    //   min: 0,
    //   startOnTick: true,
    //   endOnTick: false,
    //   minPadding: 0,
    //   maxPadding: 0.05,
    //   tickAmount: 6, // Force specific number of ticks
    //   labels: {
    //     style: {
    //       color: "#718096",
    //     },
    //   },
    // },
    yAxis: [
      {

        title: {
          text: "Emissions (MT/day)",
          style: {
            color: "#718096",
            fontSize: "12px",
            fontWeight: "500",
          },
        },
        labels: {
          style: {
            color: "#718096",
          },
          formatter: function () {
            return this.value;
          },
        },
        min: 0,
        startOnTick: true,
        minPadding: 0,
        maxPadding: 0.05,
        tickAmount: 6,
      },
      {
        
        title: {
          text: "GHG Intensity (g CO₂e/MJ)",
          style: {
            color: "#7d3c98",
            fontSize: "12px",
            fontWeight: "500",
          },
        },
        labels: {
          style: {
            color: "#7d3c98",
          },
          formatter: function () {
            return this.value;
          },
        },
        min: 0,
        startOnTick: true,
        minPadding: 0,
        maxPadding: 0.05,
        tickAmount: 6,
        opposite: true, 
      },
    ],
    // tooltip: {
    //   useHTML: true,
    //   shared: true,
    //   crosshairs: true,
    //   headerFormat:
    //     '<div style="font-weight:600;font-size:12px;margin-bottom:5px">{point.x:%b %e, %Y}</div>',
    //   pointFormat:
    //     '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y:.2f} MT/day</span></div>',
    //   backgroundColor: "#ffffff",
    //   borderColor: "#e5e7eb",
    //   borderRadius: 6,
    //   shadow: true,
    //   style: {
    //     fontSize: "12px",
    //   },
    // },
    tooltip: {
      useHTML: true,
      shared: true,
      crosshairs: true,
      headerFormat:
        '<div style="font-weight:600;font-size:12px;margin-bottom:5px">{point.x:%b %e, %Y}</div>',
      formatter: function () {
        return this.points
          .map((point) => {
            const unit =
              point.series.name === "GHG Intensity" ? "g CO₂e/MJ" : "MT/day";
            return `
              <div style="display:flex;justify-content:space-between">
                <span style="color:${point.series.color}">${point.series.name}:</span>
                <span style="font-weight:700;margin-left:10px">${point.y} ${unit}</span>
              </div>
            `;
          })
          .join("");
      },
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      borderRadius: 6,
      shadow: true,
      style: {
        fontSize: "12px",
      },
    },
    plotOptions: {
      series: {
        lineWidth: 2,
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 3,
            halo: {
              size: 0,
            },
          },
        },
        threshold: 0,
      },
    },
    series: chartData.series,
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#566573",
        fontSize: "11px",
        fontWeight: "500",
      },
    },
    credits: {
      enabled: false,
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom",
            },
            xAxis: {
              labels: {
                rotation: -30,
              },
            },
          },
        },
      ],
    },
  };

  const toggleEmissionParam = (param) => {
    setSelectedParams((prev) =>
      prev.includes(param) ? prev.filter((p) => p !== param) : [...prev, param]
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading emission data...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 text-center">
          <p>Error loading emission data</p>
          <p className="text-sm text-gray-600 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  // Always show chart if we have emissionData, even if it's zero values
  if (!emissionData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No vessel or filter selected</div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Emission Parameter Selector */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-base font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-[#27ade3]" />
          Select by:
        </label>
        <div className="flex flex-wrap gap-3">
          {Object.entries(emissionLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleEmissionParam(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${selectedParams.includes(key)
                  ? "text-white shadow-sm"
                  : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                }`}
              style={{
                backgroundColor: selectedParams.includes(key)
                  ? emissionColors[key]
                  : "",
                border: selectedParams.includes(key)
                  ? `1px solid ${emissionColors[key]}`
                  : "1px solid #e2e8f0",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart - Always show if we have data */}
      {emissionData &&
        emissionData.emissions &&
        emissionData.emissions.length > 0 ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <div className="flex items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-gray-500">No chart data available</div>
        </div>
      )}
    </div>
  );
};

export default EmissionAnalysisChart;
